import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Typography,
  Button,
  Box,
  Divider,
  CircularProgress,
  DialogActions,
} from '@mui/material';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import GROUP_TYPES from '../common/group-types';
import ColumnVisualization from './ColumnVisualization';

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(10), // Add bottom padding for fixed buttons
    position: 'relative',
    minHeight: '100%',
  },
  columnSection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
  },
  columnGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  columnChip: {
    margin: theme.spacing(0.5),
  },
  directionArrow: {
    margin: theme.spacing(1),
    fontSize: '20px',
    fontWeight: 'bold',
  },
  groupSelector: {
    minWidth: 200,
    marginBottom: theme.spacing(2),
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
  buttonContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    borderTop: '1px solid #e0e0e0',
  },
  primaryButton: {
    marginLeft: theme.spacing(1),
  },
  expansionLabel: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  mainUnitLabel: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
});

class ColumnMappingTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      columnMappings: {},
      loading: false,
      saving: false,
      expansions: [],
      columns: {
        leftExpansions: [],
        mainUnit: [],
        rightExpansions: [],
      },
      allColumns: [],
      mainUnitColumnCount: 5, // Default, will be updated from box data
    };
  }

  componentDidMount() {
    this.initializeColumnMappings();
    this.initializeFromProps();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.locationId !== this.props.locationId ||
      prevProps.expansions !== this.props.expansions ||
      prevProps.boxTypeId !== this.props.boxTypeId ||
      prevProps.columnMappings !== this.props.columnMappings
    ) {
      this.initializeColumnMappings();
      this.initializeFromProps();
    }
  }

  initializeColumnMappings = () => {
    const { columnMappings } = this.props;
    
    if (columnMappings && Array.isArray(columnMappings) && columnMappings.length > 0) {
      // Transform API response to state format
      const mappings = {};
      columnMappings.forEach((mapping) => {
        // Filter out invalid group_type_ids (like 'none', null, undefined, or empty strings)
        if (mapping && mapping.group_type_id && 
            mapping.group_type_id !== 'none' && 
            mapping.group_type_id !== 'Unassigned' &&
            typeof mapping.group_type_id === 'string' &&
            mapping.group_type_id.trim() !== '' &&
            mapping.column_id) {
          if (!mappings[mapping.group_type_id]) {
            mappings[mapping.group_type_id] = [];
          }
          mappings[mapping.group_type_id].push(mapping.column_id);
        }
      });
      this.setState({ columnMappings: mappings, loading: false });
    } else {
      // No mappings found, set empty state
      this.setState({ columnMappings: {}, loading: false });
    }
  }

  initializeFromProps = () => {
    const { expansions } = this.props;
    
    // Get main unit column count based on box type
    // Default main unit typically has 5 columns (A, B, C, D, E)
    // This can be adjusted based on box type if needed
    // Note: boxTypeId is available in props for future enhancement to determine column count dynamically
    const mainUnitColumnCount = 5; // Default, can be made dynamic based on boxTypeId if needed
    
    if (expansions) {
      const expansionsList = Array.isArray(expansions) ? expansions : [];
      
      this.setState({ 
        expansions: expansionsList, 
        mainUnitColumnCount,
      });
      this.buildColumnHierarchy(expansionsList, mainUnitColumnCount);
    }
  };

  buildColumnHierarchy = (expansions, mainUnitColumnCount = 5) => {
    // This will build the column hierarchy based on expansions
    // Structure: Left Expansions <- Main Unit -> Right Expansions
    // Column naming: Left uses double letters (AA, BB, CC...), Right uses single letters (A, B, C...)
    
    const columnStructure = {
      leftExpansions: [],
      mainUnit: [],
      rightExpansions: [],
    };

    // Main unit column count is passed as parameter (fetched from box data)
    
    // Sort expansions by box_position (negative = left, positive = right)
    const sortedExpansions = [...expansions].sort((a, b) => {
      const posA = a.box_position || 0;
      const posB = b.box_position || 0;
      return posA - posB; // Sort from most negative to most positive
    });

    // Process left expansions (box_position < 0, sorted from most negative to -1)
    const leftExpansions = sortedExpansions.filter((exp) => 
      (exp.box_position < 0) || (exp.direction === 1)
    );
    
    // Process right expansions (box_position > 0, sorted from 1 to most positive)
    const rightExpansions = sortedExpansions.filter((exp) => 
      (exp.box_position > 0) || (exp.direction === 2)
    );

    // Build left expansion columns (double letters, going backwards from main unit)
    let leftColumnOffset = 0;
    leftExpansions.reverse().forEach((expansion) => {
      const columnCount = expansion.bin_column_count || 0;
      const expansionColumns = [];
      
      // Generate double letter columns (AA, BB, CC, DD, EE, FF, etc.)
      for (let i = 0; i < columnCount; i++) {
        const letterIndex = 65 + leftColumnOffset + i; // 65 = 'A'
        const letter1 = String.fromCharCode(letterIndex);
        const letter2 = String.fromCharCode(letterIndex);
        expansionColumns.push({
          id: `${letter1}${letter2}1-${letter1}${letter2}12`, // e.g., "AA1-AA12"
          column: letter1 + letter2,
          expansionId: expansion.id,
          expansionPosition: expansion.box_position,
          isMainUnit: false,
          side: 'left',
        });
      }
      
      columnStructure.leftExpansions.push({
        expansion,
        columns: expansionColumns,
      });
      
      leftColumnOffset += columnCount;
    });

    // Build main unit columns (single letters: A, B, C, D, E, etc.)
    const mainUnitColumns = [];
    for (let i = 0; i < mainUnitColumnCount; i++) {
      const letter = String.fromCharCode(65 + i); // A, B, C, D, E...
      mainUnitColumns.push({
        id: `${letter}1-${letter}12`, // e.g., "A1-A12"
        column: letter,
        expansionId: null,
        expansionPosition: 0,
        isMainUnit: true,
        side: 'main',
      });
    }
    columnStructure.mainUnit = mainUnitColumns;

    // Build right expansion columns (single letters, continuing from main unit)
    let rightColumnOffset = mainUnitColumnCount;
    rightExpansions.forEach((expansion) => {
      const columnCount = expansion.bin_column_count || 0;
      const expansionColumns = [];
      
      // Generate single letter columns (F, G, H, I, J, K, etc.)
      for (let i = 0; i < columnCount; i++) {
        const letterIndex = 65 + rightColumnOffset + i; // Continue from main unit
        const letter = String.fromCharCode(letterIndex);
        expansionColumns.push({
          id: `${letter}1-${letter}12`, // e.g., "F1-F12"
          column: letter,
          expansionId: expansion.id,
          expansionPosition: expansion.box_position,
          isMainUnit: false,
          side: 'right',
        });
      }
      
      columnStructure.rightExpansions.push({
        expansion,
        columns: expansionColumns,
      });
      
      rightColumnOffset += columnCount;
    });

    // Store the complete column structure
    this.setState({ 
      columns: columnStructure,
      // Flatten all columns for easier access
      allColumns: [
        ...columnStructure.leftExpansions.flatMap(exp => exp.columns),
        ...columnStructure.mainUnit,
        ...columnStructure.rightExpansions.flatMap(exp => exp.columns),
      ]
    });
  };

  fetchColumnMappings = () => {
    const { locationId } = this.props;
    
    if (!locationId) {
      this.setState({ columnMappings: {}, loading: false });
      return;
    }

    this.setState({ loading: true });

    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user || !user.signInUserSession || !user.signInUserSession.idToken) {
      this.setState({ columnMappings: {}, loading: false });
      return;
    }

    fetch(process.env.REACT_APP_BASEURL + `locations/${locationId}/column-mappings`, {
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + user.signInUserSession.idToken.jwtToken,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && data.statusCode === 200 && data.mappings) {
          // Transform API response to state format
          const mappings = {};
          data.mappings.forEach((mapping) => {
            if (mapping && mapping.group_type_id && 
                mapping.group_type_id !== 'none' && 
                mapping.group_type_id !== 'Unassigned' &&
                typeof mapping.group_type_id === 'string' &&
                mapping.group_type_id.trim() !== '' &&
                mapping.column_id) {
              if (!mappings[mapping.group_type_id]) {
                mappings[mapping.group_type_id] = [];
              }
              mappings[mapping.group_type_id].push(mapping.column_id);
            }
          });
          this.setState({ columnMappings: mappings, loading: false });
        } else {
          this.setState({ columnMappings: {}, loading: false });
        }
      })
      .catch((error) => {
        console.error('Error fetching column mappings:', error);
        this.setState({ columnMappings: {}, loading: false });
      });
  };

  handleColumnGroupChange = (columnId, groupTypeId) => {
    const { columnMappings } = this.state;
    const { isSuperAdmin, userGroupType } = this.props;

    // Check if user can assign to this group
    if (!isSuperAdmin && groupTypeId !== userGroupType) {
      toast.error(this.props.t('Error.CannotAssignToOtherGroup'));
      return;
    }

    // Remove column from all groups first
    Object.keys(columnMappings).forEach((groupId) => {
      columnMappings[groupId] = columnMappings[groupId].filter(
        (id) => id !== columnId
      );
    });

    // Add to selected group if not "None"
    if (groupTypeId && groupTypeId !== 'none') {
      if (!columnMappings[groupTypeId]) {
        columnMappings[groupTypeId] = [];
      }
      columnMappings[groupTypeId].push(columnId);
    }

    this.setState({ columnMappings });
  };

  handleSave = () => {
    const { locationId } = this.props;
    const { columnMappings } = this.state;

    if (!locationId) {
      toast.error(this.props.t('Error.NoLocationSelected'));
      return;
    }

    this.setState({ saving: true });

    // Transform state to API format
    // Note: location_id is not needed in payload - API uses locationId from route parameter
    const mappings = [];
    Object.keys(columnMappings).forEach((groupTypeId) => {
      columnMappings[groupTypeId].forEach((columnId) => {
        mappings.push({
          column_id: columnId,
          group_type_id: groupTypeId,
        });
      });
    });

    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user || !user.signInUserSession || !user.signInUserSession.idToken) {
      toast.error(this.props.t('Error.SavingColumnMappings'));
      this.setState({ saving: false });
      return;
    }

    fetch(process.env.REACT_APP_BASEURL + `locations/${locationId}/column-mappings`, {
      method: 'POST',
      withCredentials: true,
      headers: {
        Authorization:
          'Bearer ' + user.signInUserSession.idToken.jwtToken,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ mappings }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && data.statusCode === 200) {
          toast.success(this.props.t('Success.ColumnMappingsSaved'));
          this.setState({ saving: false });
          // Refresh column mappings from API after successful save
          this.fetchColumnMappings();
          // If parent has a callback to refresh box data, call it
          if (this.props.onColumnMappingsSaved) {
            this.props.onColumnMappingsSaved();
          }
        } else {
          throw new Error(data.message || 'Failed to save column mappings');
        }
      })
      .catch((error) => {
        console.error('Error saving column mappings:', error);
        toast.error(error.message || this.props.t('Error.SavingColumnMappings'));
        this.setState({ saving: false });
      });
  };

  renderColumnVisualization = () => {
    const { isSuperAdmin, userGroupType, boxTypes } = this.props;
    const { columns, columnMappings } = this.state;

    console.log('[ColumnMappingTab] renderColumnVisualization - isSuperAdmin:', isSuperAdmin, 'userGroupType:', userGroupType);
    console.log('[ColumnMappingTab] Props received:', { isSuperAdmin, userGroupType, boxTypesCount: boxTypes && boxTypes.length });

    return (
      <ColumnVisualization
        columns={columns}
        columnMappings={columnMappings}
        isSuperAdmin={isSuperAdmin}
        userGroupType={userGroupType}
        boxTypes={boxTypes || []}
        onColumnGroupChange={this.handleColumnGroupChange}
      />
    );
  };

  getGroupName = (groupTypeId) => {
    if (groupTypeId === GROUP_TYPES.Pharmacy) return 'Pharmacy';
    if (groupTypeId === GROUP_TYPES.Retail) return 'Optical';
    if (groupTypeId === GROUP_TYPES.eCommerce) return 'eCommerce';
    return 'Unknown';
  };

  render() {
    const { classes, t, locationId } = this.props;
    const { loading, saving } = this.state;

    if (!locationId) {
      return (
        <Box className={classes.root}>
          <Typography>{t('Location.ColumnMapping.NoLocationSelected')}</Typography>
        </Box>
      );
    }

    // Only show loading if we're fetching column mappings
    if (loading) {
      return (
        <Box className={classes.root} sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box className={classes.root}>
        <Typography variant="h6" gutterBottom>
          {t('Location.ColumnMapping.Title')}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {t('Location.ColumnMapping.Description')}
        </Typography>

        <Divider style={{ marginBottom: 16, marginTop: 16 }} />

        {this.renderColumnVisualization()}

        <DialogActions className={classes.buttonContainer} sx={{ padding: '8px 28px 8px 8px' }}>
          <Button
            onClick={this.props.onClose || (() => {})}
            color="primary"
            className={classes.primaryButton}
          >
            {this.props.operation === 'View'
              ? t('Location.CloseButton')
              : t('Location.CancelButton')}
          </Button>
          {this.props.operation !== 'View' && (
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleSave}
              disabled={saving}
              className={classes.primaryButton}
            >
              {saving ? t('Location.ColumnMapping.Saving') : t('Location.ColumnMapping.Save')}
            </Button>
          )}
        </DialogActions>
      </Box>
    );
  }
}

ColumnMappingTab.propTypes = {
  locationId: PropTypes.string,
  isSuperAdmin: PropTypes.bool,
  userGroupType: PropTypes.string,
  expansions: PropTypes.array,
  boxTypeId: PropTypes.string,
  boxTypes: PropTypes.array,
  operation: PropTypes.string,
  onClose: PropTypes.func,
  onColumnMappingsSaved: PropTypes.func,
  columnMappings: PropTypes.array, // Array of { column_id, group_type_id }
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

export default withStyles(styles)(withTranslation()(ColumnMappingTab));




// WEBPACK FOOTER //
// ./src/components/box-list/ColumnMappingTab.js